package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.core.util.PeriodSelection
import br.com.vvrefeicoes.portal.domain.model.ConsolidatedCategory
import br.com.vvrefeicoes.portal.domain.model.ConsolidatedCorporate
import br.com.vvrefeicoes.portal.domain.model.ConsolidatedSupplier
import br.com.vvrefeicoes.portal.domain.model.ConsolidatedUnitReport
import br.com.vvrefeicoes.portal.domain.model.ConsolidatedWeek
import br.com.vvrefeicoes.portal.domain.model.OperationalEntryCatalog
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.ConsolidatedDetailedRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.statement.bodyAsText
import io.ktor.http.HttpHeaders
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class RemoteConsolidatedDetailedRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : ConsolidatedDetailedRepository {
    override suspend fun load(
        selection: PeriodSelection,
        unitId: String?,
        manager: String?,
        director: String?,
    ): List<ConsolidatedUnitReport> {
        val path = "/operational/consolidated-detailed?${panelQuery(selection, unitId, manager, director)}"
        val response = httpClient.get(baseUrl + path) { authorize() }
        if (!response.status.isSuccess()) {
            val message =
                runCatching {
                    Json { ignoreUnknownKeys = true }
                        .decodeFromString<PanelApiErrorDto>(response.bodyAsText())
                        .message
                }.getOrNull()
            error(message ?: "HTTP ${response.status.value}")
        }
        return response.body<List<ConsolidatedUnitReportDto>>().map { it.toDomain() }
    }

    private fun io.ktor.client.request.HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }
}

@Serializable
private data class ConsolidatedUnitReportDto(
    val unitId: String = "",
    val unitName: String = "",
    val realTotalCents: Long = 0,
    val plannedTotalCents: Long = 0,
    val weeks: List<ConsolidatedWeekDto> = emptyList(),
    val categories: List<ConsolidatedCategoryDto> = emptyList(),
    val corporate: ConsolidatedCorporateDto = ConsolidatedCorporateDto(),
)

@Serializable
private data class ConsolidatedWeekDto(
    val index: Int = 0,
    val label: String = "",
    val realCents: Long = 0,
    val plannedCents: Long = 0,
)

@Serializable
private data class ConsolidatedCategoryDto(
    val categoryId: String = "",
    val realCents: Long = 0,
    val plannedCents: Long = 0,
    val suppliers: List<ConsolidatedSupplierDto> = emptyList(),
)

@Serializable
private data class ConsolidatedSupplierDto(
    val name: String = "",
    val realCents: Long = 0,
    val plannedCents: Long = 0,
)

@Serializable
private data class ConsolidatedCorporateDto(
    val payrollRealCents: Long = 0,
    val payrollPlannedCents: Long = 0,
    val chargesRealCents: Long = 0,
    val chargesPlannedCents: Long = 0,
    val adminOthersRealCents: Long = 0,
    val adminOthersPlannedCents: Long = 0,
    val taxesRealCents: Long = 0,
    val taxesPlannedCents: Long = 0,
)

private fun ConsolidatedUnitReportDto.toDomain(): ConsolidatedUnitReport =
    ConsolidatedUnitReport(
        unitId = unitId,
        unitName = unitName,
        realTotalCents = realTotalCents,
        plannedTotalCents = plannedTotalCents,
        weeks = weeks.map { ConsolidatedWeek(it.index, it.label, it.realCents, it.plannedCents) },
        categories =
            categories.mapNotNull { category ->
                val title = OperationalEntryCatalog.expenseTables.firstOrNull { it.id == category.categoryId }?.title
                title?.let {
                    ConsolidatedCategory(
                        categoryId = category.categoryId,
                        title = it,
                        realCents = category.realCents,
                        plannedCents = category.plannedCents,
                        suppliers = category.suppliers.map { supplier -> ConsolidatedSupplier(supplier.name, supplier.realCents, supplier.plannedCents) },
                    )
                }
            },
        corporate =
            ConsolidatedCorporate(
                payrollRealCents = corporate.payrollRealCents,
                payrollPlannedCents = corporate.payrollPlannedCents,
                chargesRealCents = corporate.chargesRealCents,
                chargesPlannedCents = corporate.chargesPlannedCents,
                adminOthersRealCents = corporate.adminOthersRealCents,
                adminOthersPlannedCents = corporate.adminOthersPlannedCents,
                taxesRealCents = corporate.taxesRealCents,
                taxesPlannedCents = corporate.taxesPlannedCents,
            ),
    )
